import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import useLoadFBScript from './hooks/useLoadFBScript';
import { FacebookOAuthContext } from './context';

/**
 * FacebookOAuthProvider component provides Facebook OAuth context for its children.
 *
 * @param {object} props - Component properties.
 * @param {string} props.appId - The Facebook App ID.
 * @param {string} props.nonce - The nonce value for script integrity.
 * @param {function} props.onScriptLoadSuccess - Callback when the Facebook script loads successfully.
 * @param {function} props.onScriptLoadError - Callback when there is an error loading the Facebook script.
 * @param {React.ReactNode} props.children - The children components to render.
 * @returns {JSX.Element} The FacebookOAuthProvider component.
 */
export default function FacebookOAuthProvider({
	appId,
	nonce,
	onScriptLoadSuccess,
	onScriptLoadError,
	children,
}) {
	const scriptLoadedSuccessfully = useLoadFBScript({
		nonce,
		onScriptLoadSuccess,
		onScriptLoadError,
	});

	const contextValue = useMemo(() => ({
		appId,
		scriptLoadedSuccessfully,
	}), [appId, scriptLoadedSuccessfully]);

	return (
		<FacebookOAuthContext.Provider value={contextValue}>
			{children}
		</FacebookOAuthContext.Provider>
	);
}

FacebookOAuthProvider.propTypes = {
	appId: PropTypes.string.isRequired,
	nonce: PropTypes.string,
	onScriptLoadSuccess: PropTypes.func,
	onScriptLoadError: PropTypes.func,
	children: PropTypes.node.isRequired,
};

FacebookOAuthProvider.defaultProps = {
	nonce: '',
	onScriptLoadSuccess: () => {},
	onScriptLoadError: () => {},
};
